/* eslint-disable @typescript-eslint/no-explicit-any */
import { getApi, get, post, put, del } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";
import { userDataCache } from "@/features/Auth/services/UserDataCache";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";

/** Rota que a chamada usou. `embedding` é sempre `/v1/embeddings` (AB#85326). */
export type AiModelRoute = "chat" | "responses" | "embedding";

/** De onde saiu a rota — é o que diferencia typo no cadastro de ausência. */
export type AiModelRouteSource = "declared" | "default" | "invalid_declaration";

export type AiModelFailureReason =
  | "route_rejected"
  | "invalid_credential"
  | "model_not_found"
  | "provider_unavailable"
  | "gateway_rejected"
  | "unexpected";

export type AiModelOutcome =
  | { status: "skipped" }
  | {
      status: "ok";
      model: string;
      route: AiModelRoute;
      route_source: AiModelRouteSource;
    }
  | {
      status: "failed";
      model: string;
      route: AiModelRoute;
      route_source: AiModelRouteSource;
      reason: AiModelFailureReason;
      detail: string;
    };

/**
 * Escolha de modelo por propósito, gravada num caminho só (AB#85434).
 *
 * Uma integração POR PROPÓSITO (AB#85432): a organização pode conversar por um provedor e
 * indexar por outro. Sem `embedding_integration_base_id`, o embedding cai na integração do
 * conversacional — o caso comum.
 */
export interface AiModelsSelection {
  conversational_integration_base_id: string;
  conversational_model: string;
  embedding_integration_base_id?: string | null;
  embedding_model?: string | null;
}

/** O que está gravado hoje, por propósito. */
export interface AiModelsChoice {
  conversational: { integration_base_id: string; model: string } | null;
  embedding: { integration_base_id: string; model: string } | null;
}

export type AiModelTestReport =
  | {
      status:
        | "no_default_model"
        | "integration_not_found"
        | "not_llm"
        | "creds_failed"
        | "gateway_not_configured";
    }
  | {
      status: "ok";
      integration_base_id: string;
      integration_name: string;
      conversational: AiModelOutcome;
      embedding: AiModelOutcome;
    };


// Não precisamos importar SessionService aqui, vamos importá-lo dinamicamente quando necessário

/**
 * Interface que representa uma organização
 */
export interface Organization {
  id: string;
  name: string;
  website?: string;
  info?: string;
  logo_url?: string | null;
  environment?: string;
  /**
   * Role do usuário autenticado NESTA organização (AB#84509). Só `GET /organizations`
   * preenche — os demais endpoints devolvem a organização sem perspectiva de quem pede.
   */
  role?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Interface para os detalhes completos da organização incluindo integrações
 */
export interface OrganizationDetails extends Organization {
  integration?: {
    id: string;
    name: string;
    description?: string;
    logo_url?: string;
  } | null;
  /** Integração do propósito CONVERSACIONAL da organização (AB#85432). */
  integration_base?: {
    id: string;
    name: string;
    label?: string | null;
  } | null;
}

/**
 * Interface que representa o perfil de usuário com informações da organização
 */
export interface UserProfile {
  id: string;
  user_id: string;
  organization_id: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  onboarding_status?: string; // Status do onboarding: 'pending', 'in_progress', 'completed'
  created_at: string;
  updated_at: string;
}

// Interface removida para evitar duplicação e conflitos

/**
 * Classe de serviço para encapsular todas as operações relacionadas às organizações
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class OrganizationService {
  private static readonly BASE_URL = "/organizations";

  /**
   * Grava a escolha de modelo de IA da organização.
   *
   * Um POST só, no lugar das duas chamadas que esta tela fazia (organização +
   * integração): a escolha é uma decisão, e gravá-la em duas requisições deixava a
   * organização num estado misto quando a segunda falhava (AB#85434).
   */
  /** Escolha gravada, por propósito — a tela carrega duas integrações (AB#85432). */
  static async getAiModels(organizationId: string): Promise<AiModelsChoice> {
    return await get<AiModelsChoice>(`${this.BASE_URL}/${organizationId}/ai-models`);
  }

  static async updateAiModels(
    organizationId: string,
    selection: AiModelsSelection,
  ): Promise<{ conversational: string; embedding: string | null }> {
    return await put(`${this.BASE_URL}/${organizationId}/ai-models`, selection);
  }

  private static async uploadLogo(
    organizationId: string,
    logo: File,
  ): Promise<string | null> {
    const formData = new FormData();
    formData.append("logo", logo);
    try {
      const res = await post<{ logo_url: string }>(
        `${this.BASE_URL}/${organizationId}/logo`,
        formData,
      );
      return res?.logo_url ?? null;
    } catch (error) {
      console.error("[OrganizationService] v2 logo upload failed:", error);
      return null;
    }
  }

  /**
   * Tenta atualizar a sessão de autenticação
   * @returns Promise<boolean> - true se a sessão foi atualizada com sucesso
   */
  static async refreshAuthSession(): Promise<boolean> {
    try {
      // Atualizamos o cache diretamente
      try {
        await userDataCache.refreshCache();
        return true;
      } catch (cacheError) {
        console.error(
          "[OrganizationService] Erro ao atualizar cache da sessão:",
          cacheError,
        );
        return false;
      }
    } catch (error) {
      console.error("[OrganizationService] Erro ao atualizar sessão:", error);
      return false;
    }
  }

  /**
   * Obtém o ID do usuário atual usando o cache
   * @returns Promise<string> - ID do usuário
   */
  static async getUserId(): Promise<string> {
    try {
      // Usar o cache para obter o ID do usuário
      const userId = await userDataCache.getUserId();

      if (!userId) {
        console.error(
          "[OrganizationService] ID do usuário não encontrado no cache",
        );
        throw new Error("ID do usuário não encontrado");
      }

      return userId;
    } catch (error) {
      console.error(
        "[OrganizationService] Erro ao obter ID do usuário do cache:",
        error,
      );

      // Tentar atualizar a sessão se houver erro
      try {
        const refreshed = await this.refreshAuthSession();

        if (refreshed) {
          // Forçar atualização do cache diretamente
          await userDataCache.refreshCache();

          // Tentar novamente com cache atualizado
          const userId = await userDataCache.getUserId();
          if (userId) {
            return userId;
          }
        }
      } catch (refreshError) {
        console.error(
          "[OrganizationService] Erro ao atualizar sessão:",
          refreshError,
        );
      }

      throw new Error("Sessão de autenticação expirada ou inválida");
    }
  }

  // Cache para o perfil do usuário
  private static userProfileCache: UserProfile | null = null;
  private static profileCacheTime: number = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos em milissegundos

  /**
   * Pré-aquece o cache estático de userProfileCache a partir do payload do
   * aggregate `/auth/me/context`. Evita que callers imperativos
   * (getCurrentUserProfile em useEffects/handlers) disparem `/auth/me` próprio
   * logo após o bootstrap. TTL padrão (5 min) aplica normalmente.
   *
   * Chamado pelo useMeContext após resolver. Sem org → no-op (UserProfile
   * exige organization_id obrigatório).
   */
  static primeProfileCacheFromMe(meUser: {
    id: string;
    fullName: string | null;
    organizationId: string | null;
    avatarUrl: string | null;
    onboardingStatus?: string;
  }): void {
    if (!meUser.organizationId) return;
    const now = new Date().toISOString();
    const name = meUser.fullName ?? "";
    this.userProfileCache = {
      id: meUser.id,
      user_id: meUser.id,
      organization_id: meUser.organizationId,
      first_name: name.split(" ")[0] || "",
      last_name: name.split(" ").slice(1).join(" "),
      avatar_url: meUser.avatarUrl ?? undefined,
      onboarding_status: meUser.onboardingStatus,
      created_at: now,
      updated_at: now,
    };
    this.profileCacheTime = Date.now();
  }

  /**
   * Obtém o perfil do usuário atual com informações da organização
   * @returns Perfil do usuário com ID da organização
   */
  static async getCurrentUserProfile(): Promise<UserProfile> {
    try {
      // Verificar se temos o perfil em cache e se está válido
      const now = Date.now();
      if (
        this.userProfileCache &&
        now - this.profileCacheTime < this.CACHE_TTL
      ) {
        return this.userProfileCache;
      }

      // Obter dados do usuário via validate-token (legacy) ou /auth/me (v2)
      try {
        const baseUrl = API_CONFIG.API_ROOT_URL;
        const endpoint = "/auth/me";
        const validateResponse = await getApi<any>(`${baseUrl}${endpoint}`);

        // Normalizar envelope: legacy = { success, data: { user } }, v2 = User DTO flat
        const userData: Record<string, any> | undefined = validateResponse;

        if (userData?.id && userData?.organizationId) {
          const userProfile: UserProfile = {
            id: userData.id,
            user_id: userData.id,
            organization_id: userData.organizationId,
            first_name: userData.fullName?.split(" ")[0] || "",
            last_name: userData.fullName?.split(" ").slice(1).join(" ") || "",
            avatar_url: userData.avatarUrl || null,
            created_at: userData.created_at || new Date().toISOString(),
            updated_at: userData.updated_at || new Date().toISOString(),
          };

          this.userProfileCache = userProfile;
          this.profileCacheTime = now;

          return userProfile;
        }
      } catch (validateError) {
        console.error(
          "[OrganizationService] Erro ao obter dados via validate-token:",
          validateError,
        );
      }

      // Fallback: tentar o método original com userDataCache
      const sessionResult = await userDataCache.getSession();

      // Verificar se temos dados válidos
      if (sessionResult.data.session?.user) {
        // Usar o objeto de usuário retornado pela sessão
        const userData = sessionResult.data.session.user;

        if (userData.id && userData.organizationId) {
          // Criar objeto de perfil a partir dos dados do usuário
          const userProfile: UserProfile = {
            id: userData.id,
            user_id: userData.id,
            organization_id: userData.organizationId,
            first_name: userData.fullName?.split(" ")[0] || "",
            last_name: userData.fullName?.split(" ").slice(1).join(" ") || "",
            avatar_url: userData.avatarUrl,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          // Atualizar o cache
          this.userProfileCache = userProfile;
          this.profileCacheTime = now;

          return userProfile;
        } else {
          console.warn(
            "[OrganizationService] Dados incompletos do usuário na sessão JWT",
          );
          throw new Error("Dados de usuário incompletos na sessão");
        }
      } else {
        console.warn("[OrganizationService] Sessão inválida ou expirada");
        throw new Error("Sessão inválida ou expirada");
      }
    } catch (error) {
      // Silenciar erros específicos de autenticação para evitar logs desnecessários
      if (
        error instanceof Error &&
        error.message === "Usuário não autenticado"
      ) {
        console.warn(
          "[OrganizationService] Erro de autenticação silenciado:",
          error.message,
        );
        return Promise.reject(error);
      }

      console.error(
        "[OrganizationService] Erro ao buscar perfil do usuário atual:",
        error,
      );
      throw error;
    }
  }

  /**
   * Cria uma nova organização
   * @param organizationData Dados da organização a ser criada
   * @returns Organização criada junto com subscription
   */
  static async createOrganization(organizationData: {
    name: string;
    plan_id: string; // OBRIGATÓRIO - ID do plano selecionado
    industry?: string;
    size?: string;
    stage?: string;
    info?: string;
    website?: string;
    logo_url?: string;
    logo?: File;
    from_onboarding?: boolean;
  }): Promise<{
    success: boolean;
    data?: Organization;
    subscription?: any;
    message?: string;
  }> {
    try {
      const body: Record<string, unknown> = {
        name: organizationData.name,
        plan_id: organizationData.plan_id,
      };
      if (organizationData.info !== undefined)
        body.info = organizationData.info;
      if (organizationData.industry !== undefined)
        body.industry = organizationData.industry;
      if (organizationData.website !== undefined)
        body.website = organizationData.website;
      if (organizationData.size !== undefined)
        body.size = organizationData.size;
      if (organizationData.stage !== undefined)
        body.stage = organizationData.stage;
      if (organizationData.logo_url !== undefined)
        body.logo_url = organizationData.logo_url;
      if (organizationData.from_onboarding !== undefined) {
        body.from_onboarding = organizationData.from_onboarding;
      }

      const v2Response = await post<{
        organization: Organization;
        subscription?: any;
        tokens?: any;
      }>(this.BASE_URL, body);
      const created = v2Response.organization;

      // Falha no logo NÃO derruba a criação: a organização já existe, e recusar
      // aqui levaria a pessoa a tentar de novo e criar uma segunda. Mas também
      // não passa em silêncio — volta em `message`, para a tela poder avisar que
      // só o logo faltou.
      let logoWarning: string | undefined;
      if (organizationData.logo && created?.id) {
        const uploadedUrl = await this.uploadLogo(
          created.id,
          organizationData.logo,
        );
        if (uploadedUrl) {
          created.logo_url = uploadedUrl;
        } else {
          logoWarning = "Organização criada, mas o logo não pôde ser enviado.";
        }
      }

      this.userProfileCache = null;
      this.profileCacheTime = 0;

      return {
        success: true,
        data: created,
        subscription: v2Response.subscription,
        ...(logoWarning ? { message: logoWarning } : {}),
        ...(v2Response.tokens && ({ tokens: v2Response.tokens } as any)),
      };
    } catch (error: any) {
      console.error(
        "[OrganizationService] Error creating organization:",
        error,
      );
      return {
        success: false,
        message: error.message || "Failed to create organization",
      };
    }
  }

  /**
   * Obtém detalhes de uma organização pelo ID
   * @param organizationId ID da organização
   * @returns Detalhes da organização
   */
  static async getOrganizationById(
    organizationId: string,
  ): Promise<Organization> {
    try {
      const org = await get<Organization>(
        `${this.BASE_URL}/${organizationId}`,
      );
      return { ...org, logo_url: sameOriginProxyUrl(org.logo_url) };
    } catch (error) {
      console.error(
        `Erro ao buscar organização com ID ${organizationId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Obtém detalhes completos de uma organização, incluindo integrações
   * @param organizationId ID da organização
   * @returns Detalhes completos da organização
   */
  static async getOrganizationDetails(
    organizationId: string,
  ): Promise<OrganizationDetails> {
    try {
      try {
        const details = await get<OrganizationDetails>(
          `${this.BASE_URL}/${organizationId}/details`,
        );
        return { ...details, logo_url: sameOriginProxyUrl(details.logo_url) };
      } catch (apiError) {
        // Tentar novamente com um delay se for um erro de rede ou timeout
        if (
          apiError instanceof Error &&
          (apiError.message.includes("network") ||
            apiError.message.includes("timeout"))
        ) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          // Mesma rota e mesmo shape da primeira tentativa: o retry usava o
          // envelope `{data}` da rota compat, que esta rota não devolve.
          const retry = await get<OrganizationDetails>(
            `${this.BASE_URL}/${organizationId}/details`,
          );

          if (!retry) {
            throw new Error(
              "Detalhes da organização não encontrados na resposta",
            );
          }

          return { ...retry, logo_url: sameOriginProxyUrl(retry.logo_url) };
        }

        throw apiError;
      }
    } catch (error) {
      console.error(
        `[OrganizationService] Erro ao buscar detalhes da organização com ID ${organizationId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Atualiza os dados de uma organização
   * @param organizationId ID da organização
   * @param organizationData Dados da organização a serem atualizados
   * @returns Organização atualizada
   */
  static async updateOrganization(
    organizationId: string,
    organizationData: Partial<Organization> & { logo?: File },
  ): Promise<Organization> {
    // Fora do try porque o retry pós-403 no catch manda exatamente o mesmo corpo.
    const body: Record<string, unknown> = {};
    try {
      if (organizationData.name !== undefined)
        body.name = organizationData.name;
      if (organizationData.website !== undefined)
        body.website = organizationData.website;
      if (organizationData.info !== undefined)
        body.info = organizationData.info;
      if (organizationData.logo_url !== undefined)
        body.logo_url = organizationData.logo_url;

      let updated: Organization;
      if (Object.keys(body).length > 0) {
        updated = await put<Organization>(
          `${this.BASE_URL}/${organizationId}`,
          body,
        );
      } else {
        // Sem campo escalar não há PUT a fazer, mas o retorno precisa ser a
        // organização de verdade: antes era um stub `{id, name: ""}`, e quem
        // lesse `name` recebia string vazia como se fosse o nome.
        updated = await this.getOrganizationById(organizationId);
      }

      if (organizationData.logo) {
        const uploadedUrl = await this.uploadLogo(
          organizationId,
          organizationData.logo,
        );
        // Falha no upload não pode passar por sucesso: quem chamou pediu a troca
        // do logo e receberia o objeto com a url antiga, sem sinal de erro.
        if (!uploadedUrl) {
          throw new Error("Falha ao enviar o logo da organização");
        }
        updated.logo_url = uploadedUrl;
      }

      return updated;
    } catch (firstError: any) {
      // Se receber erro 403 (Forbidden), isso pode ser devido à discrepancia entre role no banco e JWT
      if (
        firstError.status === 403 ||
        (firstError.message && firstError.message.includes("403"))
      ) {
        // O retry repete a primeira tentativa passo a passo, inclusive a
        // decisão de NÃO chamar o PUT quando não há campo escalar (update só do
        // logo): mandar `PUT {}` é request sem conteúdo, e o corpo da resposta
        // não é o que precisamos — a organização vem do GET.
        //
        // Antes o retry montava um FormData e usava `putApi` (rota compat,
        // envelope `{data}`): a raiz responde flat, então `retryResponse.data`
        // vinha `undefined` e TODO retry pós-403 terminava em erro, mesmo quando
        // o servidor tinha aceitado a atualização.
        const retried =
          Object.keys(body).length > 0
            ? await put<Organization>(`${this.BASE_URL}/${organizationId}`, body)
            : await this.getOrganizationById(organizationId);

        if (!retried) {
          throw new Error(
            "Dados da organização atualizada não encontrados na resposta",
          );
        }

        // O logo continua fora do corpo: vai pelo endpoint próprio, como na
        // primeira tentativa.
        if (organizationData.logo) {
          const uploadedUrl = await this.uploadLogo(
            organizationId,
            organizationData.logo,
          );
          if (!uploadedUrl) {
            throw new Error("Falha ao enviar o logo da organização");
          }
          retried.logo_url = uploadedUrl;
        }

        return retried;
      }

      // Se não for erro 403, propaga o erro original
      throw firstError;
    }
  }

  /**
   * Obtém todos os usuários de uma organização
   * @param organizationId ID da organização
   * @returns Lista de usuários da organização
   */
  static async getUsersByOrganization(
    organizationId: string,
  ): Promise<UserProfile[]> {
    try {
      // Usar a API para buscar os perfis por organização
      const response = await getApi<{ data?: UserProfile[] }>(
        `/profiles/organization/${organizationId}`,
      );
      return response.data as UserProfile[];
    } catch (error) {
      console.error(
        `Erro ao buscar usuários da organização com ID ${organizationId}:`,
        error,
      );
      throw error;
    }
  }

  // O método updateOrganization já foi implementado acima
  // Esta duplicação foi removida para resolver conflitos da migração JWT

  /**
   * Deleta uma organização
   * @param organizationId ID da organização a ser deletada
   * @returns Resultado da operação
   */
  static async deleteOrganization(
    organizationId: string,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const v2Res = await del<{
        affected_users?: number;
        warning?: string | null;
      }>(`${this.BASE_URL}/${organizationId}`);
      this.userProfileCache = null;
      this.profileCacheTime = 0;
      return {
        success: true,
        message: v2Res?.warning ?? "Organization deleted successfully",
      };
    } catch (error: any) {
      console.error(
        "[OrganizationService] Error deleting organization:",
        error,
      );

      // Extrair mensagem de erro específica
      let errorMessage = error.message || "Failed to delete organization";

      // Verificar se é erro de produtos vinculados
      if (
        errorMessage.includes("produtos vinculados") ||
        errorMessage.includes("products")
      ) {
        errorMessage =
          "Não é possível deletar organização com produtos vinculados";
      } else if (
        errorMessage.includes("linked data") ||
        errorMessage.includes("dependencies")
      ) {
        errorMessage =
          "Não é possível deletar a organização: há dados vinculados (bases de conhecimento, apps conectados ou arquivos). Remova-os antes de excluir.";
      } else if (
        errorMessage.includes("404") ||
        errorMessage.includes("não encontrada")
      ) {
        errorMessage = "Organização não encontrada";
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Testa os modelos declarados no LLM default da organização (AB#85326).
   *
   * Exercita o caminho de produção: mesma montagem de headers, mesmo gateway. O
   * modelo que falha é RESULTADO, não erro de chamada — por isso o endpoint
   * devolve 200 com o relatório, e um throw aqui significa falha de rede ou de
   * permissão, nada sobre o cadastro.
   */
  static async testAiModels(
    organizationId: string,
    probe?: {
      purpose: "conversational" | "embedding";
      integration_base_id: string;
      model: string;
    },
  ): Promise<AiModelTestReport> {
    return post<AiModelTestReport>(
      `${this.BASE_URL}/${organizationId}/ai-models/test`,
      probe ?? {},
    );
  }
}
